/**
 * useAuth Hook
 *
 * React hook for accessing authentication context
 * Must be used within AuthProvider
 */

import { useContext } from "react";
import { AuthContext, type AuthContextValue } from "@/auth/AuthContext";

/**
 * useAuth
 *
 * Access authentication state and actions
 *
 * @returns Authentication context value
 * @throws Error if used outside AuthProvider
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const { isLoggedIn, user, userId, logout } = useAuth();
 *
 *   if (!isLoggedIn) {
 *     return <LoginPrompt />;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}!</p>
 *       <p>User ID: {userId}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example API endpoint construction
 * ```tsx
 * function ReloadTokensPage() {
 *   const { userId } = useAuth();
 *
 *   const { data: tokens } = useQuery({
 *     queryKey: ['reloadTokens', userId],
 *     queryFn: () => apiClient.get(`/users/${userId}/reload_tokens`),
 *     enabled: !!userId,
 *   });
 *
 *   return <TokenList tokens={tokens} />;
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
